import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import ComposerPanel from "@/components/ComposerPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Loader2, Eye, MessageCircle, Repeat2, ThumbsUp, MousePointerClick, TrendingUp, Download } from "lucide-react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from "recharts";

export default function AnalyticsPage() {
  const [articles, setArticles] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [summary, setSummary] = useState({});
  const [correlation, setCorrelation] = useState({ scatter: [], timeseries: [] });
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, an, s, corr] = await Promise.all([
        api.get("/articles"),
        api.get("/analytics"),
        api.get("/analytics/summary"),
        api.get("/analytics/correlation"),
      ]);
      setArticles(a.data);
      setAnalytics(an.data);
      setSummary(s.data);
      setCorrelation(corr.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const analyticsMap = Object.fromEntries(analytics.map((r) => [r.article_id, r]));

  const exportNewsletter = async (format = "md") => {
    setExporting(true);
    try {
      const res = await api.get(`/newsletter/export?days=90&format=${format}`, {
        responseType: "blob",
      });
      const ext = format === "html" ? "html" : "md";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `wisewit-newsletter.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Newsletter exported (${ext.toUpperCase()})`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// analytics</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          What's working on LinkedIn?
        </h1>
        <p className="mt-3 text-bone/60 max-w-2xl">
          After you post on LinkedIn, paste the numbers here. Lingent will track which topics
          and categories pull the most engagement so you write more of what works.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            onClick={() => exportNewsletter("md")}
            disabled={exporting}
            data-testid="export-newsletter-md-btn"
            className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export newsletter (.md)
          </Button>
          <Button
            variant="outline"
            onClick={() => exportNewsletter("html")}
            disabled={exporting}
            data-testid="export-newsletter-html-btn"
            className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-10"
          >
            <Download className="w-4 h-4 mr-2" />
            Export .html
          </Button>
        </div>

        {/* Summary KPIs */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-hairline border border-hairline">
          <KPI icon={Eye} label="Impressions" v={summary.total_impressions || 0} testid="kpi-impressions" />
          <KPI icon={ThumbsUp} label="Reactions" v={summary.total_reactions || 0} testid="kpi-reactions" />
          <KPI icon={MessageCircle} label="Comments" v={summary.total_comments || 0} testid="kpi-comments" />
          <KPI icon={Repeat2} label="Reposts" v={summary.total_reposts || 0} testid="kpi-reposts" />
          <KPI icon={MousePointerClick} label="Clicks" v={summary.total_clicks || 0} testid="kpi-clicks" />
          <KPI icon={TrendingUp} label="Tracked posts" v={summary.tracked_posts || 0} testid="kpi-tracked" />
        </div>

        {/* Top categories */}
        {summary.top_categories?.length > 0 && (
          <div className="mt-8">
            <div className="label-mono text-bone/60 mb-2">// best categories by engagement</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.top_categories.slice(0, 6).map((c) => (
                <div key={c.category} className="border border-hairline bg-surface p-4 flex items-center justify-between" data-testid={`top-cat-${c.category}`}>
                  <div>
                    <div className="font-display text-lg font-bold tracking-tightest">{c.category}</div>
                    <div className="label-mono text-bone/50 text-[10px] mt-1">{c.posts} posts</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-black text-safety">{c.engagement}</div>
                    <div className="label-mono text-bone/50 text-[10px]">score</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Correlation chart */}
        {correlation.scatter?.length > 0 && (
          <div className="mt-10 grid lg:grid-cols-2 gap-px bg-hairline border border-hairline" data-testid="analytics-correlation-chart">
            <div className="bg-ink p-5">
              <div className="label-mono text-bone/60 mb-1">// length ↔ engagement</div>
              <div className="text-xs text-bone/50 mb-3">Does longer-form content actually pull more reactions? Each dot is one article.</div>
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    type="number" dataKey="word_count" name="Words"
                    tick={{ fill: "#888", fontSize: 11 }} stroke="#444"
                    label={{ value: "Word count", position: "insideBottom", offset: -2, fill: "#888", fontSize: 11 }}
                  />
                  <YAxis
                    type="number" dataKey="engagement_score" name="Engagement"
                    tick={{ fill: "#888", fontSize: 11 }} stroke="#444"
                    label={{ value: "Engagement score", angle: -90, position: "insideLeft", fill: "#888", fontSize: 11 }}
                  />
                  <ZAxis type="number" dataKey="impressions" range={[40, 320]} name="Impressions" />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    contentStyle={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 0, color: "#eee", fontSize: 12 }}
                    formatter={(v, k) => [v, k]}
                    labelFormatter={() => ""}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="bg-ink border border-hairline p-3 text-xs">
                          <div className="font-display font-bold text-bone line-clamp-2">{p.title}</div>
                          <div className="label-mono text-[10px] text-bone/60 mt-1">{p.category}</div>
                          <div className="mt-1 text-bone/80">{p.word_count} words · {p.engagement_score} engagement · {p.impressions} imp.</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={correlation.scatter} fill="#ff6b35" fillOpacity={0.75} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-ink p-5">
              <div className="label-mono text-bone/60 mb-1">// engagement over time</div>
              <div className="text-xs text-bone/50 mb-3">Cumulative engagement and impressions across published posts.</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={correlation.timeseries} margin={{ top: 10, right: 24, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 11 }} stroke="#444" />
                  <YAxis tick={{ fill: "#888", fontSize: 11 }} stroke="#444" />
                  <Tooltip
                    contentStyle={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 0, color: "#eee", fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#888" }} />
                  <Line type="monotone" dataKey="cumulative_engagement" stroke="#ff6b35" strokeWidth={2} dot={false} name="Cumulative engagement" />
                  <Line type="monotone" dataKey="cumulative_impressions" stroke="#3b82f6" strokeWidth={2} dot={false} name="Cumulative impressions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Per-article table */}
        <div className="mt-10">
          <div className="label-mono text-bone/60 mb-3 flex items-center justify-between">
            <span>// per-article numbers (you fill these in)</span>
            <span className="text-bone/40">{articles.length} articles</span>
          </div>
          <div className="border border-hairline overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface label-mono text-bone/60 text-[10px]">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Imp.</th>
                  <th className="text-right p-3">React.</th>
                  <th className="text-right p-3">Comm.</th>
                  <th className="text-right p-3">Rep.</th>
                  <th className="text-right p-3">Clicks</th>
                  <th className="text-right p-3">Score</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {articles.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-bone/50">No articles yet. Draft one first.</td></tr>
                )}
                {articles.map((a) => {
                  const r = analyticsMap[a.id] || {};
                  const score = (r.reactions || 0) + (r.comments || 0) * 3 + (r.reposts || 0) * 5;
                  return (
                    <tr key={a.id} className="border-t border-hairline hover:bg-surface" data-testid={`analytics-row-${a.id}`}>
                      <td className="p-3 max-w-md">
                        <div className="font-display font-bold tracking-tightest line-clamp-1">{a.title}</div>
                      </td>
                      <td className="p-3 label-mono text-[10px] text-bone/60">{a.category}</td>
                      <td className="p-3 label-mono text-[10px]">{a.status}</td>
                      <td className="p-3 text-right tabular-nums">{r.impressions || 0}</td>
                      <td className="p-3 text-right tabular-nums">{r.reactions || 0}</td>
                      <td className="p-3 text-right tabular-nums">{r.comments || 0}</td>
                      <td className="p-3 text-right tabular-nums">{r.reposts || 0}</td>
                      <td className="p-3 text-right tabular-nums">{r.clicks || 0}</td>
                      <td className="p-3 text-right font-display font-bold text-safety tabular-nums">{score}</td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing({ article: a, current: r })}
                          data-testid={`edit-analytics-${a.id}`}
                          className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-8"
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <AnalyticsEditDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <LingentFloatingIcon onOpen={() => {}} />
    </div>
  );
}

const KPI = ({ icon: Icon, label, v, testid }) => (
  <div className="bg-ink p-5" data-testid={testid}>
    <div className="flex items-center gap-2 label-mono text-bone/50">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <div className="font-display text-2xl font-black mt-1 tabular-nums">{v}</div>
  </div>
);

function AnalyticsEditDialog({ editing, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      const r = editing.current || {};
      setForm({
        impressions: r.impressions || 0,
        reactions: r.reactions || 0,
        comments: r.comments || 0,
        reposts: r.reposts || 0,
        clicks: r.clicks || 0,
        notes: r.notes || "",
      });
    }
  }, [editing]);

  if (!editing) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/analytics/${editing.article.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-ink border-hairline text-bone max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-bone">
            Update analytics
          </DialogTitle>
          <DialogDescription className="label-mono text-[10px] text-bone/50 line-clamp-1">
            {editing.article.title}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["impressions", "Impressions"],
            ["reactions", "Reactions"],
            ["comments", "Comments"],
            ["reposts", "Reposts"],
            ["clicks", "Clicks"],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="label-mono text-bone/70">{label}</Label>
              <Input
                type="number"
                min={0}
                value={form[k] ?? 0}
                onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) || 0 })}
                className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10"
                data-testid={`analytics-input-${k}`}
              />
            </div>
          ))}
        </div>
        <div>
          <Label className="label-mono text-bone/70">Notes</Label>
          <Textarea
            rows={3}
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
            data-testid="analytics-input-notes"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-hairline bg-transparent label-mono" data-testid="analytics-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono" data-testid="analytics-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
